import { cn } from '@/app/lib/utils';
import CustomSelect, { Option, CustomSelectProps } from '../ui/CustomSelect';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getPersonas } from '@/api/personas';
import { useParams } from 'next/navigation';

type Props = {
  defaultValue: any;
  label?: string;
  required?: boolean;
  wrapperClassName?: string;
  className?: string;
  clearable?: boolean;
  disabled?: boolean;
  placeholder?: string;
  multiple?: boolean;
  searchable?: boolean;
  onOptionSelect: (option: any) => void;
  fetchSelectedOptionOnMount?: boolean;
} & Partial<CustomSelectProps>;

const PersonaSelect = ({
  defaultValue,
  label,
  required,
  wrapperClassName,
  className,
  clearable,
  disabled,
  placeholder,
  onOptionSelect,
  fetchSelectedOptionOnMount,
  multiple,
  searchable,
  ...props
}: Props) => {
  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const [isOptionCbTriggered, setIsOptionCbTriggered] = useState(false);
  const { clientId } = useParams<{ clientId: string }>();
  const [search, setSearch] = useState('');
  const { data: options, isLoading } = useQuery({
    queryKey: ['personas', clientId, search],
    queryFn: () => getPersonas({ clientId: clientId || '', limit: 100, search: search }),
    enabled: !!clientId,
  });

  const optionsList = useMemo(() => {
    const list: Option[] =
      options?.data?.map(option => ({
        id: option.id,
        value: option.short_name || '-',
      })) || [];
    if (selectedOption && list.find(o => o.id === selectedOption.id) === undefined) {
      list.unshift({
        id: selectedOption.id.toString(),
        value: selectedOption.value.toString(),
      });
    }
    if (multiple && selectedOptions) {
      // find missing options
      const missingOptions = selectedOptions.filter(o => !list.some(c => c.id === o.id));
      list.unshift(...missingOptions);
    }
    return list;
  }, [options, selectedOption, selectedOptions, multiple]);

  useEffect(() => {
    if (fetchSelectedOptionOnMount && defaultValue && !isOptionCbTriggered && !isLoading) {
      let values: Option | Option[] | null = null;
      if (multiple) {
        values = optionsList.filter(o => defaultValue.includes(o.id)) || [];
      } else {
        values = optionsList.find(o => o.id === defaultValue) || null;
      }
      if ((Array.isArray(values) && values.length !== 0) || values !== null) {
        if (multiple && Array.isArray(values)) {
          setSelectedOptions(values);
        } else if (!multiple && values && !Array.isArray(values)) {
          setSelectedOption(values);
        }
        onOptionSelect(values);
        setIsOptionCbTriggered(true);
      } else if (defaultValue || (Array.isArray(defaultValue) && defaultValue.length !== 0)) {
        // call api
      }
    }
  }, [
    fetchSelectedOptionOnMount,
    defaultValue,
    optionsList,
    onOptionSelect,
    isOptionCbTriggered,
    isLoading,
    multiple,
  ]);

  return (
    <div className={cn('flex flex-col', wrapperClassName)}>
      {label && (
        <label className="text-sm font-medium mb-1.5 text-muted-foreground">
          {label}
          {required && <span className="text-red-600 ml-1">*</span>}
        </label>
      )}
      <CustomSelect
        options={optionsList || []}
        placeholder={placeholder}
        defaultValue={defaultValue}
        onOptionSelect={option => {
          if (multiple) {
            setSelectedOptions(option as Option[]);
          } else {
            setSelectedOption(option as Option);
          }
          onOptionSelect(option as Option);
        }}
        clearable={clearable}
        disabled={disabled}
        triggerClassNames={className}
        multiple={multiple}
        searchable={searchable}
        onSearch={setSearch}
        searchLoading={isLoading}
        {...props}
      />
    </div>
  );
};

export default PersonaSelect;
